/**
 * Invitations: who has been asked in, and the one moment their link exists.
 *
 * `POST /admin/invitations` is the only response that ever carries the link —
 * the server stores a digest of the token and mails the raw value. So the link
 * is shown here once, with a copy button, and an invitation whose link was lost
 * is revoked and reissued rather than recovered.
 */

import { useAuth } from '@/api/auth'
import { useMemo, useState } from 'react'
import type { FormEvent, ReactElement } from 'react'

import {
  useAdminUsers,
  useCreateInvitation,
  useInvitations,
  useRevokeInvitation,
  useRooms,
} from '@/api/queries'
import type {
  CreateInvitationRequest,
  Invitation,
  InvitationKind,
  InvitationRole,
} from '@/api/types'
import {
  Badge,
  Button,
  Checkbox,
  EmptyState,
  Modal,
  Select,
  Spinner,
  TextField,
  useToast,
} from '@/components/ui'
import { formatRelative } from '@/lib/format'
import { QrCode } from '@/components/QrCode'
import { QrPosterModal } from '@/components/QrPosterModal'

import {
  AdminPage,
  Card,
  ErrorNotice,
  describeError,
  readField,
  readText,
  useCopy,
} from './AdminLayout'
import styles from './admin.module.css'

const ROLES: ReadonlyArray<{ value: InvitationRole; label: string; hint: string }> = [
  { value: 'member', label: 'Member', hint: 'A normal account on this server.' },
  {
    value: 'guest',
    label: 'Guest',
    hint: 'Reaches only the channels they are added to, and cannot browse the server.',
  },
  {
    value: 'admin',
    label: 'Administrator',
    hint: 'Can open these pages and manage every account except the owner’s.',
  },
]

type InvitationState = 'pending' | 'accepted' | 'revoked' | 'expired'

function invitationState(invitation: Invitation, now: number): InvitationState {
  if (invitation.accepted_at !== null) return 'accepted'
  if (invitation.revoked_at !== null) return 'revoked'
  if (Date.parse(invitation.expires_at) <= now) return 'expired'
  return 'pending'
}

const STATE_LABEL: Record<InvitationState, string> = {
  pending: 'Waiting',
  accepted: 'Accepted',
  revoked: 'Revoked',
  expired: 'Expired',
}

const STATE_TONE: Record<InvitationState, 'info' | 'success' | 'neutral' | 'warning'> = {
  pending: 'info',
  accepted: 'success',
  revoked: 'neutral',
  expired: 'warning',
}

function roleLabel(role: InvitationRole): string {
  return ROLES.find((entry) => entry.value === role)?.label ?? role
}

function toRole(value: string): InvitationRole {
  return value === 'admin' || value === 'guest' ? value : 'member'
}

export function InvitationsPage(): ReactElement {
  const invitations = useInvitations()
  const revoke = useRevokeInvitation()
  const toast = useToast()

  const [showAll, setShowAll] = useState(false)
  const [revoking, setRevoking] = useState<Invitation | null>(null)

  const inviters = useAdminUsers()
  const inviterNames = useMemo(() => {
    const names = new Map<string, string>()
    for (const user of inviters.data ?? []) names.set(user.id, user.display_name)
    return names
  }, [inviters.data])

  const rows = useMemo(() => {
    // Read the clock inside the memo: expiry is a boundary that only matters
    // when the list itself changes, and a table that re-derives every tick
    // would flicker for a difference nobody can see.
    const now = Date.now()

    const all = (invitations.data ?? []).map((invitation) => ({
      invitation,
      state: invitationState(invitation, now),
    }))

    const filtered = showAll ? all : all.filter((row) => row.state === 'pending')

    return filtered.sort(
      (a, b) => Date.parse(b.invitation.created_at) - Date.parse(a.invitation.created_at),
    )
  }, [invitations.data, showAll])

  const confirmRevoke = (): void => {
    if (revoking === null) return

    revoke.mutate(revoking.id, {
      onSuccess: () => {
        toast.success(
          revoking.kind === 'code'
            ? 'The registration code has been revoked.'
            : `The invitation to ${revoking.email ?? 'that address'} has been revoked.`,
        )
        setRevoking(null)
      },
      onError: (error: unknown) => {
        toast.error(describeError(error, 'That invitation could not be revoked.'))
      },
    })
  }

  return (
    <AdminPage
      title="Invitations"
      lead="Invite by email link or create a code to share yourself. Both expire and can create only one account."
    >
      <NewInvitation />

      <Card
        title={showAll ? 'All invitations' : 'Waiting to be accepted'}
        actions={
          <Button onClick={() => setShowAll((current) => !current)}>
            {showAll ? 'Show only waiting' : 'Show all'}
          </Button>
        }
      >
        {invitations.isError ? (
          <ErrorNotice error={invitations.error} title="The invitations could not be loaded">
            <Button onClick={() => void invitations.refetch()}>Try again</Button>
          </ErrorNotice>
        ) : invitations.isPending ? (
          <Spinner size="lg" label="Loading invitations" />
        ) : rows.length === 0 ? (
          <EmptyState
            title={showAll ? 'No invitations' : 'Nothing is waiting'}
            description={
              showAll
                ? 'Nobody has been invited to this server yet.'
                : 'Every invitation has been accepted, revoked or has expired.'
            }
          />
        ) : (
          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th scope="col">Invitation</th>
                  <th scope="col">Type</th>
                  <th scope="col">Role</th>
                  <th scope="col">Uses</th>
                  <th scope="col">Channels</th>
                  <th scope="col">Expires</th>
                  <th scope="col">Status</th>
                  <th scope="col">
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map(({ invitation, state }) => (
                  <tr key={invitation.id}>
                    <th scope="row">
                      {invitation.kind === 'qr'
                        ? invitation.name ?? 'QR-code poster'
                        : invitation.kind === 'code'
                          ? 'Registration code'
                          : invitation.email}
                      {invitation.requires_approval ? (
                        <span className={styles.hint}> (Requires admin approval)</span>
                      ) : null}
                      {invitation.invited_by_id !== null ? (
                        <span className={styles.hint}>
                          Invited by {inviterNames.get(invitation.invited_by_id) ?? 'an admin'}
                        </span>
                      ) : null}
                    </th>
                    <td>
                      <Badge tone={invitation.kind === 'qr' ? 'info' : 'neutral'}>
                        {invitation.kind.toUpperCase()}
                      </Badge>
                    </td>
                    <td>{roleLabel(invitation.role)}</td>
                    <td className={styles.numeric}>
                      {invitation.kind === 'qr'
                        ? `${invitation.use_count ?? 0} / ${invitation.max_uses ?? '∞'}`
                        : invitation.accepted_at !== null
                          ? '1 / 1'
                          : '0 / 1'}
                    </td>
                    <td className={styles.numeric}>
                      {invitation.room_ids.length === 0 ? (
                        <span className={styles.muted}>None</span>
                      ) : (
                        invitation.room_ids.length
                      )}
                    </td>
                    <td className={styles.nowrap} title={invitation.expires_at}>
                      {formatRelative(invitation.expires_at)}
                    </td>
                    <td>
                      <Badge tone={STATE_TONE[state]}>{STATE_LABEL[state]}</Badge>
                    </td>
                    <td>
                      <div className={styles.cellActions}>
                        <Button
                          size="sm"
                          variant="danger"
                          disabled={state !== 'pending'}
                          onClick={() => setRevoking(invitation)}
                        >
                          Revoke
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Modal
        open={revoking !== null}
        onClose={() => setRevoking(null)}
        title="Revoke this invitation?"
        size="sm"
        footer={
          <>
            <Button onClick={() => setRevoking(null)}>Cancel</Button>
            <Button variant="danger" loading={revoke.isPending} onClick={confirmRevoke}>
              Revoke invitation
            </Button>
          </>
        }
      >
        {revoking === null ? null : (
          <p>
            {revoking.kind === 'code' ? (
              <>The code stops working immediately. Its audit record is kept.</>
            ) : (
              <>
                The link sent to <strong>{revoking.email}</strong> stops working immediately. The
                record is kept, so the audit trail still shows who invited them and when it was
                withdrawn.
              </>
            )}
          </p>
        )}
      </Modal>
    </AdminPage>
  )
}

// ── Creating one ────────────────────────────────────────────────────────────

function NewInvitation(): ReactElement {
  const { server } = useAuth()
  const canEmail = server?.capabilities?.email === true
  const [sendEmail, setSendEmail] = useState(canEmail)
  const create = useCreateInvitation()
  const { rooms, isLoading: roomsLoading } = useRooms()
  const toast = useToast()
  const copy = useCopy()

  const [kind, setKind] = useState<InvitationKind>('email')
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [maxUses, setMaxUses] = useState('')
  const [requiresApproval, setRequiresApproval] = useState(false)
  const [role, setRole] = useState<InvitationRole>('member')
  const [roomIds, setRoomIds] = useState<readonly string[]>([])
  const [posterOpen, setPosterOpen] = useState(false)
  const [credential, setCredential] = useState<
    | { kind: 'email'; email: string; value: string; queued: boolean }
    | { kind: 'code'; value: string }
    | { kind: 'qr'; name: string; value: string; requiresApproval: boolean }
    | null
  >(null)

  const channels = useMemo(
    () =>
      rooms
        .filter((room) => room.kind === 'channel' && room.archived_at === null && room.is_local)
        .sort((a, b) => (a.name ?? '').localeCompare(b.name ?? '')),
    [rooms],
  )

  const toggleRoom = (roomId: string, checked: boolean): void => {
    setRoomIds((current) =>
      checked ? [...current, roomId] : current.filter((id) => id !== roomId),
    )
  }

  const submit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault()

    const parsedMaxUses = maxUses.trim() === '' ? undefined : parseInt(maxUses.trim(), 10)

    const body: CreateInvitationRequest = {
      kind,
      role,
      ...(kind === 'email' ? { email: email.trim(), send_email: canEmail && sendEmail } : {}),
      ...(kind === 'qr'
        ? {
            name: name.trim() === '' ? undefined : name.trim(),
            max_uses: Number.isNaN(parsedMaxUses) ? undefined : parsedMaxUses,
            requires_approval: requiresApproval,
          }
        : {}),
    }
    if (roomIds.length > 0) body.room_ids = [...roomIds]

    create.mutate(body, {
      onSuccess: (invitation) => {
        const url = readText(readField(invitation, 'invite_url'))
        const code = readText(readField(invitation, 'invite_code'))
        setEmail('')
        setName('')
        setMaxUses('')
        setRequiresApproval(false)
        setRoomIds([])

        if (kind === 'qr' && url !== null) {
          setCredential({
            kind: 'qr',
            name: invitation.name ?? 'QR-code invite',
            value: url,
            requiresApproval: invitation.requires_approval ?? false,
          })
          toast.success('QR invitation created.')
          return
        }

        if (kind === 'code' && code !== null) {
          setCredential({ kind: 'code', value: code })
          toast.success('Registration code created.')
          return
        }

        if (kind === 'email' && url !== null && invitation.email !== null) {
          setCredential({ kind: 'email', email: invitation.email, value: url, queued: invitation.email_delivery === 'queued' })
          toast.success('Invitation created.')
          return
        }

        if (kind === 'email' && invitation.email !== null) {
          // A server that does not return the link has mailed it and nothing
          // else; saying so beats showing an empty box.
          toast.success(`Invitation created for ${invitation.email}.`)
          return
        }

        toast.error('The server created the invitation but did not return its code.')
      },
      onError: (error: unknown) => {
        toast.error(describeError(error, 'That invitation could not be created.'))
      },
    })
  }

  const fieldError = (field: string): string | null => {
    const messages = readField(readField(create.error, 'fieldErrors'), field)
    return Array.isArray(messages) ? readText(messages[0]) : null
  }

  const selectedRole = ROLES.find((entry) => entry.value === role)

  return (
    <Card
      title="Invite somebody"
      lead={
        kind === 'qr'
          ? 'Generate a multi-use QR code poster for kindergarten, housing association or group onboarding.'
          : kind === 'email'
            ? 'They choose their own username and password from the link.'
            : 'Generate a one-time code. They enter their own name, email and password.'
      }
    >
      <div className={styles.stack}>
        <form className={styles.stack} onSubmit={submit} noValidate>
          <div className={styles.inviteRow}>
            <Select
              label="Invitation type"
              value={kind}
              onChange={(event) =>
                setKind(
                  event.target.value === 'qr'
                    ? 'qr'
                    : event.target.value === 'code'
                      ? 'code'
                      : 'email',
                )
              }
              options={[
                { value: 'email', label: 'Email link' },
                { value: 'code', label: 'Registration code' },
                { value: 'qr', label: 'QR-code (Poster / Multi-use)' },
              ]}
            />
            {kind === 'qr' ? (
              <TextField
                className={styles.grow}
                label="Poster / Group Title"
                placeholder="e.g. Borettslag Oppgang B / Barnehagestart"
                value={name}
                onChange={(event) => setName(event.target.value)}
                autoComplete="off"
              />
            ) : null}
            {kind === 'qr' ? (
              <TextField
                label="Max uses (optional)"
                placeholder="Unlimited"
                type="number"
                value={maxUses}
                onChange={(event) => setMaxUses(event.target.value)}
                autoComplete="off"
              />
            ) : null}
            {kind === 'email' ? (
              <TextField
                className={styles.grow}
                label="Email address"
                type="email"
                required
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                error={fieldError('email')}
                autoComplete="off"
              />
            ) : null}
            <Select
              label="Role"
              value={role}
              onChange={(event) => setRole(toRole(event.target.value))}
              options={ROLES.map((entry) => ({ value: entry.value, label: entry.label }))}
              hint={selectedRole?.hint}
            />
          </div>

          {kind === 'qr' ? (
            <div style={{ marginTop: '8px' }}>
              <Checkbox
                label="Require admin approval for each member (Moderation queue)"
                checked={requiresApproval}
                onChange={(event) => setRequiresApproval(event.target.checked)}
              />
              <p className={styles.hint}>
                {requiresApproval
                  ? 'Members create their account by scanning the code, but cannot see channels until an admin approves them.'
                  : 'Instant access — members who scan the code are added directly into channels.'}
              </p>
            </div>
          ) : null}

          <fieldset className={styles.fieldset}>
            <legend className={styles.legend}>Add them to channels (optional)</legend>
            {roomsLoading ? (
              <Spinner label="Loading channels" />
            ) : channels.length === 0 ? (
              <p className={styles.small}>
                You are not a member of any channel to add them to. They can be added once they
                are in.
              </p>
            ) : (
              <div className={styles.checkList}>
                {channels.map((room) => (
                  <Checkbox
                    key={room.id}
                    label={room.name ?? room.slug ?? 'Channel'}
                    checked={roomIds.includes(room.id)}
                    onChange={(event) => toggleRoom(room.id, event.target.checked)}
                  />
                ))}
              </div>
            )}
            <p className={styles.hint}>Only channels you are a member of are listed.</p>
          </fieldset>

          {kind === 'email' ? canEmail ? <Checkbox label="Send invitation by email" checked={sendEmail}
            onChange={(event) => setSendEmail(event.target.checked)} /> : <p className={styles.hint}>Email delivery is not configured. Create a link to share.</p> : null}
          <div className={styles.cellActions}>
            <Button type="submit" variant="primary" loading={create.isPending}>
              {kind === 'code' ? 'Generate code' : kind === 'qr' ? 'Generate QR code' : canEmail && sendEmail ? 'Send invitation' : 'Create invitation link'}
            </Button>
          </div>
        </form>

        {credential === null ? null : (
          <div className={styles.secret}>
            <div>
              <p className={styles.noticeTitle}>
                {credential.kind === 'code'
                  ? 'Registration code'
                  : credential.kind === 'qr'
                    ? `QR invitation: ${credential.name}`
                    : `The link for ${credential.email}`}
              </p>
              <p className={styles.small}>
                {credential.kind === 'code'
                  ? 'Shown once. Share it through a channel you trust.'
                  : credential.kind === 'qr'
                    ? credential.requiresApproval
                      ? 'Multi-use QR code ready. Users who scan must be approved by an admin.'
                      : 'Multi-use QR code ready. Users who scan are admitted immediately.'
                    : credential.queued
                      ? 'Queued for email delivery. You can also copy this link now.'
                      : 'Shown once. Copy this link and share it with the person you are inviting.'}
              </p>
            </div>
            {credential.kind === 'qr' ? (
              <div style={{ margin: '16px 0', textAlign: 'center' }}>
                <QrCode value={credential.value} size={180} />
              </div>
            ) : null}
            <div className={styles.secretValue}>
              <span className={styles.secretText}>{credential.value}</span>
              <Button
                onClick={() =>
                  copy(
                    credential.value,
                    credential.kind === 'code'
                      ? 'The registration code'
                      : credential.kind === 'qr'
                        ? 'The QR invitation link'
                        : 'The invitation link',
                  )
                }
              >
                Copy
              </Button>
              {credential.kind === 'qr' ? (
                <Button variant="primary" onClick={() => setPosterOpen(true)}>
                  Print poster
                </Button>
              ) : null}
            </div>
            <div className={styles.cellActions}>
              <Button size="sm" onClick={() => setCredential(null)}>
                Hide
              </Button>
            </div>

            {credential.kind === 'qr' ? (
              <QrPosterModal
                open={posterOpen}
                onClose={() => setPosterOpen(false)}
                title={credential.name}
                url={credential.value}
                serverName={server?.display_name ?? server?.server_id ?? null}
                requiresApproval={credential.requiresApproval}
              />
            ) : null}
          </div>
        )}
      </div>
    </Card>
  )
}
